import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Button } from '@mui/material';
import { Search, ChevronRight, Calendar, Users, Share2, Menu, FileText, Bell } from 'lucide-react';

import { PlaybookIcon, HomeIcon } from '@labradorsports/assets';

import { ProfilePicture } from '../../shared/interface/index.js';
import { NavLinkWithQuery } from '../../shared/structural/index.js';
import { Selectors, useLogoutMutation } from '../../store/index.js';
import LoggedInMenu from '../logged-in-menu/logged-in-menu.js';
import NotificationBadge from '../notification-badge/notification-badge.js';
import Notifications from '../notifications/notifications.js';
import { GlobalSearchBox } from '../search/index.js';

const NavbarLink = ({
    text,
    icon,
    path,
    onClick,
    expanded = false,
    desktopOnly = false,
}: {
    text?: string;
    icon?: React.ReactNode;
    path: string;
    onClick?: () => void;
    expanded?: boolean;
    desktopOnly?: boolean;
}) => {
    return (
        <NavLinkWithQuery
            to={path}
            className={({ isActive }) =>
                `nav-link navbar-item ${isActive ? 'active' : ''} ${desktopOnly ? 'desktop-only' : ''}`
            }
            onClick={onClick}
        >
            {expanded ? null : (
                <>
                    <span className="nav-link-bar" />
                    {icon}
                </>
            )}
            {text ? <span className="nav-link-text">{text}</span> : null}
        </NavLinkWithQuery>
    );
};

interface Props {
    expanded: boolean;
    onLogin: boolean;
    setExpanded: (expanded: boolean) => void;
}

const Navbar: FC<Props> = ({ expanded, setExpanded: _setExpanded, onLogin }) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const user = useSelector(Selectors.user);
    const currentTeam = useSelector(Selectors.currentTeam);
    const [logout] = useLogoutMutation();
    const location = useLocation();
    const [showMenu, setShowMenu] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    const setExpanded = (expanded: boolean) => {
        _setExpanded(expanded);
        setShowNotifications(false);
    };

    useEffect(() => {
        setExpanded(false);
    }, [location.pathname]);

    const links: any[] = [
        {
            text: 'Playbook',
            icon: <PlaybookIcon className="navbar-item-icon" />,
            path: '/playbook',
        },
        {
            text: 'Schedule',
            icon: <Calendar className="navbar-item-icon" />,
            path: '/schedule',
        },
        {
            text: 'Roster',
            icon: <Users className="navbar-item-icon" />,
            path: '/team',
        },
    ];

    if (currentTeam) {
        links.push({
            text: 'Documents',
            icon: <FileText className="navbar-item-icon" />,
            path: '/documents',
            desktopOnly: true,
        });
    }

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const searchClick = () => {
        handleExpandClick();

        if (searchRef.current) {
            searchRef.current.focus();
        }
    };

    const notificationsClick = () => {
        handleExpandClick();

        setShowNotifications(showMenu ? !showNotifications : true);
    };

    const hideMenu = () => setShowMenu(false);

    if (!onLogin && !user) {
        return null;
    }

    return (
        <nav className={`site-navbar${expanded ? ' expanded' : ''} ${onLogin ? 'logged-out' : ''}`}>
            <div className="navbar-content">
                <div className="nav-links top-links">
                    <NavbarLink icon={<HomeIcon className="navbar-item-icon" />} path="/feed" />
                    <button className="navbar-item d-none d-lg-block" onClick={searchClick}>
                        <Search className="navbar-item-icon" />
                    </button>
                    {links.map(({ text, icon, path, desktopOnly }) => (
                        <NavbarLink key={text} icon={icon} path={path} desktopOnly={desktopOnly} />
                    ))}
                    <button className="d-lg-none navbar-item menu-button" onClick={() => setShowMenu(!showMenu)}>
                        <Menu className="navbar-item-icon" />
                        <NotificationBadge />
                    </button>
                </div>

                {user ? (
                    <div className="bottom-links">
                        <NavbarLink icon={<Share2 className="navbar-item-icon" />} path="/community" />
                        <button className="navbar-item notification-button" onClick={notificationsClick}>
                            <Bell className="navbar-item-icon" />
                            <NotificationBadge />
                        </button>
                        <NavbarLink icon={<ProfilePicture uid={user.uid} />} path="/profile" />
                        <button className="navbar-item" onClick={handleExpandClick} style={{ cursor: 'pointer' }}>
                            <ChevronRight className="expand-nav-icon navbar-item-icon" />
                        </button>
                    </div>
                ) : null}
                <LoggedInMenu onClose={hideMenu} open={showMenu} onNotificationsClick={notificationsClick} />
            </div>

            <div className="expanded-content">
                <div className="top-links">
                    <NavbarLink text="Home" path="/feed" expanded />
                    <div className="navbar-item">{expanded ? <GlobalSearchBox /> : null}</div>
                    {links.map(({ text, path }) => (
                        <NavbarLink key={text} text={text} path={path} expanded />
                    ))}
                </div>
                <div className="bottom-links">
                    <NavbarLink text="Community" path="/community" expanded />
                    <button className="nav-link navbar-item" onClick={() => setShowNotifications(true)}>
                        Notifications
                    </button>
                    <NavbarLink text="Profile" path="/profile" expanded />
                    <div className="navbar-item text-right">
                        <Button onClick={() => logout()}>Logout</Button>
                    </div>
                </div>
            </div>

            <Notifications onClose={() => setShowNotifications(false)} visible={showNotifications} />
        </nav>
    );
};

export default Navbar;
